import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, ChevronRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Badge } from '@/components/ui/badge';
import MobileTooltip from '@/components/MobileTooltip';
import CountryFilter from '@/components/filters/CountryFilter';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/components/utils/translations';
import { isUntrustedAccountLogoUrl, accountNameInitials } from '@/lib/marketExperience';
import AccountFitBadge from '@/components/accounts/AccountFitBadge';

export default function TopAccountsCard({ accounts, country = "all", onCountryChange, countryOptions = [{ label: "All Countries", value: "all" }], currentTenant }) {
  const { t } = useTranslation();
  console.log('🏢 TopAccountsCard received:', {
    accountsLength: accounts?.length || 0,
    country,
    currentTenant,
    sample: accounts?.slice(0, 3).map(a => ({
      name: a.account_name,
      fit_score: a.account_fit_score,
      tenant_id: a.tenant_id,
      country: a.country
    }))
  });

  const topAccounts = useMemo(() => {
    const preFiltered = (accounts || [])
      .filter(acc => acc.is_visible !== false && acc.account_fit_score);
    
    const topCandidates = preFiltered
      .sort((a, b) => (b.account_fit_score || 0) - (a.account_fit_score || 0))
      .slice(0, 5);

    console.log('🏢 TopAccountsCard filtered:', {
      before: accounts?.length || 0,
      afterTopFilter: topCandidates.length,
      countryFilter: country,
      topAccounts: topCandidates.map(a => ({
        name: a.account_name,
        score: a.account_fit_score,
        country: a.country
      }))
    });

    return topCandidates;
  }, [accounts, country]);

  const isCalmedicaTenant = currentTenant === 'calmedica';

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-2 md:pb-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1">
            <Building2 className="w-4 h-4 text-[#00C2C2] flex-shrink-0" />
            <CardTitle className="text-sm font-semibold text-[#0A1A2F]">{t('recommendedAccounts')}</CardTitle>
            <MobileTooltip 
              content={<p className="text-xs">{t('recommendedAccountsTooltip')}</p>}
              side="bottom"
              align="start"
              className="bg-[#0A1A2F] text-white border-0 shadow-lg"
            >
              <button className="p-1 rounded-full hover:bg-gray-100 transition-colors touch-manipulation">
                <Info className="w-3 h-3 text-[#5C6B7A]" />
              </button>
            </MobileTooltip>
          </div>
          
          {countryOptions.length > 1 && (
            <CountryFilter
              value={country}
              onChange={onCountryChange || (() => {})}
              options={countryOptions}
              className="w-auto min-w-[8rem] max-w-[11rem] h-8 text-xs gap-1"
            />
          )}
        </div>
        <Link to={createPageUrl('Accounts')} className="text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">
          {t('viewAll')}
        </Link>
      </CardHeader>
      <CardContent className="pt-0 px-3 md:px-6">
        <div className="space-y-2">
          {topAccounts.map((acc) => {
            const isCHUBordeaux = isCalmedicaTenant && acc.account_name === 'CHU de Bordeaux';
            
            return (
              <Link
                key={acc.id}
                to={createPageUrl(`AccountFinal?id=${acc.id}`)}
                className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                  {!isUntrustedAccountLogoUrl(acc.logo_url) ? (
                    <img 
                      src={acc.logo_url} 
                      alt={acc.account_name}
                      className="w-8 h-8 object-contain rounded-md border p-0.5 bg-white flex-shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md flex-shrink-0 border border-gray-200">
                      <span className="text-[10px] font-semibold text-[#5C6B7A]">
                        {accountNameInitials(acc.account_name)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm text-[#0A1A2F] truncate group-hover:text-[#00C2C2] transition-colors">
                        {acc.account_name}
                      </p>
                      {isCHUBordeaux && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button 
                              className="cursor-help flex-shrink-0"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <Badge className="bg-blue-500 text-white text-[10px] h-5 px-1.5 rounded-full whitespace-nowrap">
                                {t('client')}
                              </Badge>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top"
                            align="center"
                            className="bg-[#0A1A2F] text-white border-0 shadow-lg"
                          >
                            <p className="text-xs">Ambulatory Surgery client</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <p className="text-xs text-[#5C6B7A]">{acc.country}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 md:gap-1.5 flex-shrink-0">
                  <MobileTooltip
                    content={acc.score_rationale ? (
                      <>
                        <p className="text-xs font-semibold text-[#00C2C2] mb-1">{t('scoreRationale')}</p>
                        <p className="text-xs leading-relaxed">{acc.score_rationale}</p>
                      </>
                    ) : null}
                    side="left"
                    align="center"
                    className="bg-[#0A1A2F] text-white border-0 shadow-lg w-[90vw] max-w-[600px]"
                  >
                    <button 
                      className="touch-manipulation"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <AccountFitBadge account={acc} className="text-[10px] h-5 whitespace-nowrap px-1.5 cursor-help" />
                    </button>
                  </MobileTooltip>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#00C2C2] transition-colors hidden sm:block" />
                </div>
              </Link>
            );
          })}
          {topAccounts.length === 0 && (
            <div className="text-center py-4 text-[#5C6B7A]">
              <Building2 className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-xs">{t('noAccounts')}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
